import { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Box, Card, CardContent, Typography, Stack, Chip, Avatar, IconButton,
  Grid, CircularProgress, Alert, TextField, InputAdornment,
  Autocomplete, Divider, Button, Tooltip,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  HowToReg as RegisterIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Save as SaveIcon,
  Undo as UndoIcon,
  ErrorOutline as MissingIcon,
  CheckCircle as DoneIcon,
} from '@mui/icons-material';
import API from '../../BaseURL';
import swal from '../../utils/swal';

// ─────────────────────────────────────────────────────────────────
// Register Assignment — who takes the daily register for each class.
//
// Register duty is stored in its own `register_teacher` table, NOT in
// `class_teacher`. That one already means "owns the report-card
// remarks" to the examination module, so sharing it would have shown
// every existing remarks owner as pre-assigned here.
//
// Nothing is written until Save is pressed. Picks are held as pending
// changes so a screenful of decisions is committed in one transaction
// — and so a half-made choice is never persisted.
//
// Two classes per row: a school has a dozen-plus classes and a
// full-width row each meant scrolling through most of a screen to
// reach J.H.S-3.
// ─────────────────────────────────────────────────────────────────
export default function RegisterAssignmentPage() {
  const [accYer, setAccYer] = useState('');
  const [classes, setClasses] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [teacherNotice, setTeacherNotice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [onlyUnassigned, setOnlyUnassigned] = useState(false);

  // cla -> user_id | null. Only classes the user actually touched.
  const [pending, setPending] = useState({});

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      // scope=register — the academic year from Settings, which is the
      // label routes/register.js looks duty up by. Asking for the
      // examination year here would stamp assignments the register can
      // never find, since the two calendars drift apart.
      const yr = await API.get('/teacher-assignments/active-year', {
        params: { scope: 'register' },
      });
      const year = yr.data?.acc_yer || '';
      setAccYer(year);

      const t = await API.get('/teacher-assignments/teachers');
      setTeachers(t.data?.teachers || []);
      if (t.data?.requires_role) {
        setTeacherNotice('No role is marked as a teacher role yet. Open Roles, edit the role '
                       + 'your teachers use and tick "teacher role" — then they appear here.');
      } else if (t.data?.requires_users) {
        setTeacherNotice('No user accounts hold a teacher role yet. Create them on the Users page first.');
      } else {
        setTeacherNotice(null);
      }

      if (year) {
        const r = await API.get('/teacher-assignments/register-duty', {
          params: { acc_yer: year },
        });
        setClasses(r.data?.classes || []);
      }
      setPending({});
    } catch (e) {
      setError(e?.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Many-to-many: each class holds a SET of teacher ids. What a row
  // shows is the pending set if it was touched, else what's saved.
  const savedIds = (c) => (c.teachers || []).map(t => Number(t.user_id));

  const effectiveIds = (c) =>
    Object.prototype.hasOwnProperty.call(pending, c.cla)
      ? pending[c.cla]
      : savedIds(c);

  const sameSet = (a, b) => {
    if (a.length !== b.length) return false;
    const sa = [...a].sort((x, y) => x - y);
    const sb = [...b].sort((x, y) => x - y);
    return sa.every((v, i) => v === sb[i]);
  };

  const isDirty = (c) =>
    Object.prototype.hasOwnProperty.call(pending, c.cla)
    && !sameSet(pending[c.cla], savedIds(c));

  const pick = (cla, picked) => {
    setPending(prev => ({ ...prev, [cla]: (picked || []).map(t => Number(t.id)) }));
  };

  const dirtyList = useMemo(
    () => classes.filter(isDirty),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [classes, pending]
  );

  const save = async () => {
    if (!dirtyList.length) return;
    setSaving(true);
    try {
      const res = await API.put('/teacher-assignments/register-duty', {
        acc_yer: accYer,
        assignments: dirtyList.map(c => ({ cla: c.cla, user_ids: pending[c.cla] })),
      });
      // Fold the saved values into the base list so rows stop showing
      // as unsaved without needing a full refetch.
      setClasses(prev => prev.map(c => {
        if (!Object.prototype.hasOwnProperty.call(pending, c.cla)) return c;
        const ids = pending[c.cla] || [];
        return {
          ...c,
          teachers: ids.map(uid => {
            const t = teachers.find(x => Number(x.id) === Number(uid));
            return { user_id: uid, name: t?.full_name, email: t?.email };
          }),
        };
      }));
      setPending({});
      swal.success(res.data?.message || 'Register assignments saved');
    } catch (e) {
      swal.error(swal.pickError(e, 'Could not save the assignments'));
    } finally {
      setSaving(false);
    }
  };

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    return classes.filter(c => {
      const ids = effectiveIds(c);
      if (onlyUnassigned && ids.length) return false;
      if (!q) return true;
      const names = ids
        .map(id => teachers.find(x => Number(x.id) === id))
        .map(t => `${t?.full_name || ''} ${t?.email || ''}`)
        .join(' ');
      return `${c.cla} ${c.class_t || ''} ${names}`.toLowerCase().includes(q);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classes, search, onlyUnassigned, pending, teachers]);

  const stats = useMemo(() => {
    const active = classes.filter(c => c.is_active);
    return {
      total: active.length,
      covered: active.filter(c => effectiveIds(c).length).length,
      learners: active.reduce((n, c) => n + (c.students || 0), 0),
      // Total teacher-to-class links, which is the number that grows
      // once classes start carrying assistants.
      links: active.reduce((n, c) => n + effectiveIds(c).length, 0),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classes, pending]);

  // The Autocomplete needs the teacher OBJECTS for the current set.
  const teacherObjs = (c) =>
    effectiveIds(c)
      .map(id => teachers.find(t => Number(t.id) === id))
      .filter(Boolean);

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Header — compact, and the Save button lives here so it is
          reachable without scrolling to the bottom of the grid. */}
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }} flexWrap="wrap">
        <Avatar sx={{ bgcolor: 'primary.main', width: 36, height: 36 }}>
          <RegisterIcon fontSize="small" />
        </Avatar>
        <Box sx={{ flexGrow: 1, minWidth: 210 }}>
          <Typography variant="h6" fontWeight={800} lineHeight={1.2}>
            Register Assignment
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Who marks the daily register{accYer ? ` — ${accYer}` : ''}
          </Typography>
        </Box>
        <Chip size="small"
          label={`${stats.covered}/${stats.total} classes`}
          color={stats.total && stats.covered === stats.total ? 'success' : 'default'}
          sx={{ fontWeight: 700 }} />
        <Chip size="small" variant="outlined" label={`${stats.links} assignments`} />
        <Chip size="small" variant="outlined" label={`${stats.learners} learners`} />
        {!!dirtyList.length && (
          <Tooltip title="Discard the changes you haven't saved">
            <Button size="small" color="inherit" startIcon={<UndoIcon />}
              onClick={() => setPending({})}>
              Reset
            </Button>
          </Tooltip>
        )}
        <Button
          variant="contained" size="small"
          startIcon={saving ? <CircularProgress size={15} color="inherit" /> : <SaveIcon />}
          disabled={!dirtyList.length || saving || !accYer}
          onClick={save}
        >
          {saving ? 'Saving…'
            : dirtyList.length ? `Save ${dirtyList.length} change${dirtyList.length === 1 ? '' : 's'}`
            : 'Save'}
        </Button>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
      {teacherNotice && <Alert severity="warning" sx={{ mb: 1.5 }}>{teacherNotice}</Alert>}
      {!accYer && !loading && (
        <Alert severity="error" sx={{ mb: 1.5 }}>
          No active academic year found. Set the current year and term in the
          Examination module first — assignments are stored per year.
        </Alert>
      )}
      {!!dirtyList.length && (
        <Alert severity="info" sx={{ mb: 1.5, py: 0.4 }}>
          <strong>{dirtyList.length}</strong> unsaved change
          {dirtyList.length === 1 ? '' : 's'} — nothing is stored until you press Save.
        </Alert>
      )}

      <Card sx={{ mb: 1.5 }}>
        <CardContent sx={{ py: 1.2, '&:last-child': { pb: 1.2 } }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2} alignItems="center">
            <TextField
              size="small" fullWidth
              placeholder="Search a class or teacher…"
              value={search} onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
                ),
                endAdornment: search ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearch('')}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />
            <Button
              size="small"
              variant={onlyUnassigned ? 'contained' : 'outlined'}
              color={onlyUnassigned ? 'warning' : 'inherit'}
              startIcon={<MissingIcon />}
              onClick={() => setOnlyUnassigned(v => !v)}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Needs a teacher
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {loading ? (
        <Box sx={{ p: 8, textAlign: 'center' }}><CircularProgress /></Box>
      ) : !shown.length ? (
        <Card sx={{ p: 5, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {classes.length
              ? 'No class matches that filter.'
              : 'No classes defined yet — add them on the Classes page first.'}
          </Typography>
        </Card>
      ) : (
        // Three classes per row on a wide screen, dropping to two then
        // one as it narrows. Three only kicks in at lg (1200px+):
        // below that each card is too tight for a name to be readable
        // in the picker.
        <Grid container spacing={1.2}>
          {shown.map((c) => {
            const dirty = isDirty(c);
            const picked = teacherObjs(c);
            const assigned = picked.length > 0;
            return (
              <Grid item xs={12} sm={6} lg={4} key={c.cla}>
                <Card variant="outlined" sx={{
                  borderRadius: 1.5,
                  borderColor: dirty ? 'info.main' : assigned ? 'success.light' : 'warning.light',
                  borderWidth: dirty ? 2 : 1,
                  bgcolor: (t) => dirty ? alpha(t.palette.info.main, 0.05) : 'background.paper',
                }}>
                  <Stack direction="row" spacing={0.8} alignItems="center" sx={{ px: 1.1, py: 0.9 }}>
                    {/* Class + roll. Fixed width so the pickers line up
                        down each column; kept narrow because at three
                        cards per row there is little to spare. */}
                    <Box sx={{ width: 88, flexShrink: 0 }}>
                      <Typography variant="body2" fontWeight={800} noWrap title={c.cla}>
                        {c.cla}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap
                        sx={{ display: 'block' }}>
                        {c.students} on roll{!c.is_active ? ' · off' : ''}
                      </Typography>
                    </Box>

                    {/* Multiple teachers per class — a class teacher
                        plus any assistants. All of them will see this
                        class's register. */}
                    <Autocomplete
                      multiple
                      disableCloseOnSelect
                      size="small"
                      sx={{ flexGrow: 1, minWidth: 0 }}
                      options={teachers}
                      value={picked}
                      disabled={!accYer || saving}
                      getOptionLabel={(t) => t.full_name || t.email || `#${t.id}`}
                      isOptionEqualToValue={(a, b) => Number(a.id) === Number(b.id)}
                      onChange={(_, sel) => pick(c.cla, sel)}
                      renderOption={(props, t) => (
                        <li {...props} key={t.id}>
                          <Typography variant="body2" noWrap>
                            {t.full_name || t.email}
                          </Typography>
                        </li>
                      )}
                      renderTags={(value, getTagProps) =>
                        value.map((t, i) => (
                          <Chip
                            {...getTagProps({ index: i })}
                            key={t.id}
                            size="small"
                            label={t.full_name || t.email}
                            // The first one picked reads as the class
                            // teacher, the rest as assistants.
                            color={i === 0 ? 'primary' : 'default'}
                            variant={i === 0 ? 'filled' : 'outlined'}
                            sx={{ height: 21, fontSize: 11, fontWeight: i === 0 ? 700 : 500 }}
                          />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField {...params}
                          placeholder={picked.length ? 'Add another…' : 'Pick teacher(s)…'} />
                      )}
                    />

                    <Box sx={{ width: 20, flexShrink: 0, textAlign: 'center' }}>
                      {dirty
                        ? <Tooltip title="Unsaved — press Save">
                            <SaveIcon sx={{ fontSize: 17, color: 'info.main' }} />
                          </Tooltip>
                        : assigned
                          ? <Tooltip title={`${picked.length} teacher${picked.length === 1 ? '' : 's'} — saved`}>
                              <DoneIcon sx={{ fontSize: 17, color: 'success.main' }} />
                            </Tooltip>
                          : <Tooltip title="No teacher yet"><MissingIcon sx={{ fontSize: 17, color: 'warning.main' }} /></Tooltip>}
                    </Box>
                  </Stack>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      <Divider sx={{ mt: 2, mb: 1 }} />
      <Typography variant="caption" color="text.secondary">
        A class may have several teachers — the first one picked shows as the class
        teacher, the rest as assistants — and a teacher may hold several classes.
        Everyone listed on a class will see its register. This is separate from the
        class teacher set in Examination Settings, which controls report-card remarks.
      </Typography>
    </Box>
  );
}
